import { useState, useEffect } from 'react'
import { FlaskConical, X, Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { motion, AnimatePresence } from 'framer-motion'

// ── Variant definitions ────────────────────────────────────────────────────────
// CSS lives in index.css under [data-variant="..."] selectors.
// Removing <DesignLab /> from AppShell reverts everything.

// Standalone variants (no dark/light split)
const SOLO = [
  {
    id: 'glass',
    name: 'Glass',
    description: 'Frosted surfaces, animated B&W depth',
    swatches: ['#010101', '#2a2a2a', 'rgba(255,255,255,0.15)'],
  },
]

// Grouped variants — each has a dark and light mode
const GROUPED = [
  {
    name: 'Bento',
    description: 'Drop shadows, organized grid',
    dark:  { id: 'bento-dark',  swatches: ['#0f0f0f', '#191919', '#f0f0f0'] },
    light: { id: 'bento-light', swatches: ['#efefef', '#ffffff', '#111111'] },
  },
  {
    name: 'Neomorph',
    description: 'Extruded surfaces, Space Mono',
    dark:  { id: 'neomorph-dark',  swatches: ['#1e1e1e', '#2a2a2a', '#e0e0e0'] },
    light: { id: 'neomorph-light', swatches: ['#e0e0e0', '#efefef', '#222222'] },
  },
]

// No variants currently need dynamic font loading
const MONO_VARIANTS = new Set([])

function loadFont(href) {
  if (document.querySelector(`link[href="${href}"]`)) return
  const link = document.createElement('link')
  link.rel = 'stylesheet'
  link.href = href
  document.head.appendChild(link)
}

export function applyVariant(id) {
  document.documentElement.setAttribute('data-variant', id === 'default' ? '' : id)
  if (MONO_VARIANTS.has(id)) {
    loadFont('https://fonts.googleapis.com/css2?family=Space+Mono:ital,wght@0,400;0,700;1,400&display=swap')
  }
  try { localStorage.setItem('prjct-design-variant', id) } catch {}
  window.dispatchEvent(new CustomEvent('design-variant-change', { detail: id }))
}

// Apply on load — default to neomorph-dark if no saved preference
try {
  const saved = localStorage.getItem('prjct-design-variant')
  applyVariant(saved && saved !== 'default' ? saved : 'neomorph-dark')
} catch {}

// ── Component ──────────────────────────────────────────────────────────────────
export function DesignLab() {
  const [open, setOpen] = useState(false)
  const [recapOpen, setRecapOpen] = useState(false)
  const [active, setActive] = useState(() => {
    try { return localStorage.getItem('prjct-design-variant') || 'neomorph-dark' } catch { return 'neomorph-dark' }
  })

  useEffect(() => {
    const obs = new MutationObserver(() => {
      setRecapOpen(!!document.body.dataset.recapOpen)
    })
    obs.observe(document.body, { attributes: true, attributeFilter: ['data-recap-open'] })
    return () => obs.disconnect()
  }, [])

  function select(id) {
    setActive(id)
    applyVariant(id)
  }

  if (recapOpen) return null

  return (
    <div className="fixed bottom-5 right-4 z-[60] flex flex-col items-end gap-2">
      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            initial={{ opacity: 0, scale: 0.95, y: 6 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 6 }}
            transition={{ duration: 0.16, ease: [0.23, 1, 0.32, 1] }}
            className="w-60 bg-surface border border-border rounded-2xl shadow-2xl shadow-black/60 overflow-hidden p-3 origin-bottom-right"
          >
            <p className="text-[10px] font-semibold text-text-muted uppercase tracking-widest px-1 mb-2.5">
              Design Lab
            </p>

            {/* Solo variants */}
            <div className="space-y-1 mb-3">
              {SOLO.map((v) => (
                <button
                  key={v.id}
                  onClick={() => select(v.id)}
                  className={cn(
                    'w-full flex items-center gap-2.5 px-2.5 py-2 rounded-xl text-left transition-colors active:scale-[0.98]',
                    active === v.id
                      ? 'bg-accent/10 border border-accent/25'
                      : 'hover:bg-surface-raised border border-transparent'
                  )}
                >
                  <div className="flex gap-px flex-shrink-0">
                    {v.swatches.map((c, i) => (
                      <div key={i} className="w-3.5 h-3.5 rounded-sm border border-white/[0.06]" style={{ background: c }} />
                    ))}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={cn('text-xs font-semibold', active === v.id ? 'text-accent' : 'text-text')}>{v.name}</p>
                    <p className="text-[10px] text-text-faint leading-tight">{v.description}</p>
                  </div>
                  {active === v.id && <Check size={11} className="text-accent flex-shrink-0" />}
                </button>
              ))}
            </div>

            {/* Grouped variants with dark/light toggle */}
            <div className="border-t border-border/60 pt-2.5 space-y-2.5">
              {GROUPED.map((group) => {
                const isDark  = active === group.dark.id
                const isLight = active === group.light.id
                const isActive = isDark || isLight
                return (
                  <div key={group.name}>
                    <div className="flex items-center gap-1.5 px-1 mb-1.5">
                      <p className={cn('text-xs font-semibold flex-1', isActive ? 'text-accent' : 'text-text')}>{group.name}</p>
                      <p className="text-[10px] text-text-faint truncate">{group.description}</p>
                    </div>
                    <div className="grid grid-cols-2 gap-1.5">
                      {[
                        { mode: group.dark,  label: 'Dark' },
                        { mode: group.light, label: 'Light' },
                      ].map(({ mode, label }) => {
                        const isSelected = active === mode.id
                        return (
                          <button
                            key={mode.id}
                            onClick={() => select(mode.id)}
                            className={cn(
                              'flex flex-col gap-1.5 px-2 py-2 rounded-xl border transition-colors active:scale-[0.97]',
                              isSelected
                                ? 'bg-accent/10 border-accent/25'
                                : 'hover:bg-surface-raised border-transparent'
                            )}
                          >
                            <div className="flex gap-px">
                              {mode.swatches.map((c, i) => (
                                <div key={i} className="w-4 h-4 rounded-sm border border-white/[0.06] flex-shrink-0" style={{ background: c }} />
                              ))}
                            </div>
                            <div className="flex items-center gap-1">
                              <span className={cn('text-[10px] font-semibold leading-none', isSelected ? 'text-accent' : 'text-text-muted')}>
                                {label}
                              </span>
                              {isSelected && <Check size={9} className="text-accent flex-shrink-0" />}
                            </div>
                          </button>
                        )
                      })}
                    </div>
                  </div>
                )
              })}
            </div>

            <div className="mt-3 pt-2.5 border-t border-border/60">
              <p className="text-[9px] text-text-faint px-1 leading-snug">
                CSS-only overrides. Remove &lt;DesignLab /&gt; to ship default.
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toggle button */}
      <motion.button
        whileTap={{ scale: 0.97 }}
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex items-center gap-2 px-4 py-2.5 rounded-full text-sm font-medium shadow-lg border transition-colors duration-150',
          open || active !== 'default'
            ? 'bg-accent border-accent/80 text-black'
            : 'bg-surface border-border text-text-muted hover:text-text hover:border-border/80',
        )}
      >
        {open ? <X size={14} /> : <FlaskConical size={14} />}
        <span>Design</span>
      </motion.button>
    </div>
  )
}
